import Link from "next/link";
import { useTranslations } from "next-intl";

import * as Profile from "@/components/auth/Profile";
import { teamPageUrl } from "@/urls";
import { twMerge } from "@/utils/twMerge";

import type { MentionScope } from "./useMentionAccess";
import type { UserShort } from "@/api";

export function MentionCard({
  username,
  user,
  missingAccess,
  onInvite,
}: {
  username: string;
  user?: UserShort;
  /** Set when the mentioned person cannot see what they were mentioned in. */
  missingAccess?: MentionScope;
  onInvite?: () => void;
}) {
  const t = useTranslations();
  const displayUsername = user?.username || username;
  const profileUrl = teamPageUrl({ teamPermalink: displayUsername });

  return (
    <div
      className={twMerge(
        "flex flex-col min-w-[200px] max-w-[300px] py-2 px-3 rounded-12",
        "bg-block-bg shadow-md dark:shadow-block-inverted",
        "font-sans tracking-tight",
      )}
    >
      <Link href={profileUrl} className="group flex flex-row items-center gap-2 py-1">
        <Profile.Userpic
          name={user?.name || username}
          src={user?.avatarUrls?.[64]}
          className="shrink-0"
        />
        <span className="flex flex-col min-w-0 leading-[1.2]">
          {user?.name && (
            <span className="truncate text-[15px] font-semibold group-hover:underline">
              {user.name}
            </span>
          )}
          <span className="truncate text-[13px] text-gray-500">@{displayUsername}</span>
        </span>
      </Link>
      {missingAccess && (
        <>
          <hr className="my-2 border-t border-gray-200 dark:border-gray-700" />
          <div className="flex flex-row items-center gap-1 my-1 text-[13px] text-gray-500">
            <span className="flex-1 leading-4">
              {t.rich(missingAccess === "team" ? "mention.notTeamMember" : "mention.noAccess", {
                user: (text) => (
                  <span className="font-semibold text-gray-700 dark:text-gray-300">{text}</span>
                ),
                name: user?.name ?? `@${displayUsername}`,
              })}
            </span>
            {onInvite && (
              <button
                type="button"
                onClick={onInvite}
                className={twMerge(
                  "flex items-center h-7 px-2 rounded-6 whitespace-nowrap",
                  "text-[14px] text-blue-500 hover:bg-sidebar-button-hover-bg",
                )}
              >
                {t("mention.invite")}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
